import { TenantJob, TenantPerformableConfig } from './TenantJob';
import { ZodVersionedMetadata, ZodVersionedSchema } from '~/lib/zod';
import type { CustomerOrderMetadataPolicy } from '~/lib/customer-order-metadata';
import type { CustomerActionMetadataPolicy, CustomerJobMetadataPolicy } from '~/lib/customer-job-metadata';
import { TenantOrder } from '~/tenants/common/TenantOrder';
import { TenantOrderContext } from '~/tenants/common/TenantOrderContext';
import {
  TenantActionFormProps,
  TenantCustomerFormProps,
  TenantOrderFormProps,
  TenantPerformableFormProps,
  TenantPerformableSubmitProps
} from '~/tenants/common/form';
import { ReactElement, ReactNode } from 'react';
import { TimeZone } from '~/lib/enum';
import { TenantCustomer, TenantProvider } from '~/tenants/common/TenantPersona';
import { DistanceAddress, GeocodedAddress, OrderLinePhase, ProviderStaticConfig } from '~/lib/model';
import { Holiday } from '~common/holidays/holidays';
import TenantWrapper from '~/tenants/common/TenantWrapper';
import { DeliverableType } from '~common/model/Deliverable';
import { DateTime } from 'luxon';
import { ScheduleAppointmentPopupProps } from '~/components/schedule/components/ScheduleAppointmentPopup';

export enum TenantId {
  BEYOND_RE_MARKETING = 'bre',
  NE_PHOTO = 'nep',
  DEMO = 'demo',
  METROPLEX_360 = 'mpx',
  MPI = 'mpi',
  PLUSH = 'pic',
  MOTION_CITY_MEDIA = 'mcm',
  TWIST_TOURS = 'twt',
  TWIST_TOURS_V2 = 'twt2',
  MAINE_LINE_MEDIA = 'mlm',
  BLEENK_MEDIA = 'bmd'
}

// used for testing purposes
export const TENANT_DEFAULT_ID = TenantId.TWIST_TOURS;

type TenantPerformableForm<O extends TenantOrderContext> = (props: TenantPerformableFormProps<O>) => ReactNode;
type TenantPerformableSubmit<O extends TenantOrderContext> = (props: TenantPerformableSubmitProps<O>) => ReactNode;

export interface TenantPerformable<
  P extends TenantPerformableConfig = TenantPerformableConfig,
  O extends TenantOrderContext = TenantOrderContext,
  V extends TenantProvider = TenantProvider,
> {
  config: TenantPerformableConfig;
  form: TenantPerformableForm<O>;
  submit?: TenantPerformableSubmit<O>;
  addons?: string[];
  job: new (order: O, id?: string, metadata?: ZodVersionedMetadata<P['schema']>, provider?: V) => TenantJob<O, P>;
}

export interface TenantActionConfig {
  id: string;
  name: string;
  deliverable: DeliverableType[];
  job: TenantPerformableConfig;
  form: (props: TenantActionFormProps<any>) => ReactNode;
  /** What a customer's post may set on the job (lib/customer-job-metadata.ts): the form's fields, and what it fixes. */
  customer: CustomerActionMetadataPolicy;
}

export interface TenantPackageConfig<T extends TenantOrderContext = TenantOrderContext> {
  id: string;
  name: string;
  description: string;
  configurable?: boolean;
  performables: Array<
    | {
        config: TenantPerformableConfig;
        metadata: Record<string, unknown>;
      }
    | TenantPerformableConfig
  >;
  percentage?: number;
  amount?: TMoney;
  hideSavings?: boolean;
  eligible?: (order: T) => boolean;
}

export interface TenantLocationConfig {
  slug: string;
  name: string;
  address: GeocodedAddress;
  timezone: TimeZone;
}

export interface TenantBrand {
  name: string;
  domain: string;
  location?: string;
  emailFrom?: string;
  logoRaster: string;
  logoVector?: string;
}

/**
 * THIS OBJECT SHIPS TO THE BROWSER. root.tsx imports the registry, so every
 * tenant's register.ts (all tenants, not just the one being served) is in the
 * public client bundle, and a loader that returns a tenant serialises it into
 * the page. Nothing here may be a credential: API keys, tokens and anything
 * else that grants access live in tenants/common/credentials.server.ts and are
 * read through its helpers on the server. The build stops the ones it can
 * recognise (dev/ci/client-bundle-scan.ts); a key with no recognisable shape,
 * a UUID say, would pass it, so the rule here is the real guard.
 */
export interface TenantConfig<C extends TenantOrderContext<any, any, any> = TenantOrderContext<any, any, any>> {
  readonly id: TenantId;
  readonly name: string;
  readonly legal: string;
  readonly domain: string;
  readonly email: string;
  readonly emailFrom?: string;
  readonly emailTech?: string;
  readonly phone: TPhone;
  readonly tax?: TTaxRate;
  /**
   * The $16 vertical video ("the reel", .server/reel-upsell.ts) is a taxable
   * line, at `tax`, on this company's orders (founder, 14 Oct 2026: taxed for
   * Twist, and no one else). Off: untaxed, as it always was.
   */
  readonly reelTaxable?: boolean;
  /**
   * Buying the branded 9:16 ("the reel", .server/reel-upsell.ts) needs the
   * agent profile it is branded for to have a HEADSHOT (23 Oct 2026). Without
   * one the reel's end card has no photo of the agent (it shows the listing's
   * hero photo instead, ReelOutro). On: the reel offer asks for the headshot
   * first (logo optional, the brokerage's name is the fallback) and the
   * purchase refuses until it is there. Off: the headshot is asked for but
   * not required. ON for MCM (the demo tenant); OFF for Twist until the
   * founder decides.
   */
  readonly reelRequiresHeadshot?: boolean;
  readonly phaseDefault?: OrderLinePhase;
  readonly emailEnable?: boolean;
  readonly emailCustomer?: string;
  readonly emailMissingMessage?: string;
  readonly customerPortalDisabledMessage?: string;
  readonly emailDeliverAppointment?: boolean;
  readonly emailOrderSubmittedDisabled?: boolean;
  readonly emailOrderCompleteDisabled?: boolean;
  readonly emailInvoice?: TEmail;
  readonly nylasProviderInvite?: boolean;
  readonly userback?: boolean;
  readonly wizardProvider?: boolean;
  readonly wizardDay?: boolean;
  /**
   * Customers pick their times from the earlier picker (Anytime, Morning,
   * Afternoon, after/before a specific time, a specific window) instead of the
   * arrival windows (lib/legacy-times.ts).
   */
  readonly legacyTimePicker?: boolean;
  /**
   * Customers can't change a placed order themselves — no reschedule, hold
   * or cancel online, and no deleting it from their order list (the order
   * wizard refuses a placed order for every company). Every such path
   * refuses on the server and tells them to contact the office, which makes
   * the change from its own screens (.server/policy/order-change-policy.ts).
   * Orders not yet placed are the customer's as before. Absent = self-serve
   * under the change policy.
   */
  readonly customerChangesDisabled?: boolean;
  readonly wizardPackage?: boolean;
  readonly wizardHideSavings?: boolean;
  readonly disableReview?: boolean;
  readonly wizardServicesTitle?: string;
  readonly holidays?: Holiday[];
  readonly markaEnable?: boolean;
  readonly inboxEnabled?: boolean;
  readonly inboxDaily?: boolean;
  readonly inboxConversation?: boolean;
  readonly legacyVendorId?: number;
  readonly validateRequested?: boolean;
  readonly defaultWindow?: number;
  readonly rainforestEnable?: boolean;
  readonly brochureDisabled?: boolean;
  readonly rainforestMerchantId?: string;
  readonly rainforestMerchantApplicationId?: string;
  readonly scheduleStartTime?: TTime24;
  readonly scheduleEndTime?: TTime24;
  readonly scheduleMinDaytime?: TTime24;
  readonly scheduleMaxDaytime?: TTime24;
  readonly scheduleNotifyChecked?: boolean;
  readonly scheduleJobColor?: boolean;
  readonly scheduleDayLock?: boolean;
  /**
   * Code default for the "Still not locked an hour after scheduling" alert
   * (AlertType.INTAKE_LOCK_DELAY). Absent = on. The office can override it
   * on the Company settings page (company.alert_unlocked_time), and a saved
   * value wins over this default (lib/order-alert-settings.ts).
   */
  readonly alertUnlockedTime?: boolean;
  readonly scheduleHideCustomer?: boolean;
  readonly scheduleHover?: (props: ScheduleAppointmentPopupProps) => ReactElement;
  readonly smsPrefix?: string;
  readonly locations: TenantLocationConfig[];
  readonly logoRaster: string;
  readonly logoVector?: string;
  readonly background?: string;
  readonly azureApplicationId?: string;
  readonly orderNumberMinimum?: number;
  readonly orderNumberPadding?: number;
  readonly payoutStart?: TDateISO;
  readonly providerClass: new (tenant: TenantWrapper, id: string, data: any) => TenantProvider<any, any>;
  readonly customerClass: new (id: string, name: string, metadata: any) => TenantCustomer<any>;
  readonly orderClass: new (tenant: TenantConfig, context: C) => TenantOrder<C>;
  readonly forms: {
    wizardType?: () => ReactNode;
    payment?: () => ReactNode;
    times?: (props: { context: C }) => ReactNode;
    orderConfigure: (props: TenantOrderFormProps<C>) => ReactNode;
    provider: () => ReactNode;
    customer?: (props: TenantCustomerFormProps) => ReactNode;
    onboarding?: () => ReactNode;
    delivery?: (props: { context: C }) => ReactNode;
  };
  readonly providers: Record<string, ProviderStaticConfig>;
  readonly sortProviders?: 'alphabetical' | 'preserve';
  readonly orderSchema: ZodVersionedSchema;
  /**
   * Which order-metadata fields a customer's post may write, and which are the
   * office's alone (lib/customer-order-metadata.ts). Every field of
   * `orderSchema`, listed once.
   */
  readonly customerOrderMetadata: CustomerOrderMetadataPolicy;
  readonly customerSchema: ZodVersionedSchema;
  readonly packages: Array<{ config: TenantPackageConfig<any>; form: () => ReactNode }>;
  readonly performables: Record<string, TenantPerformable<any, any, any>>;
  /**
   * Per service id: which job fields a customer's post may set, and which are
   * the office's alone (lib/customer-job-metadata.ts). Every field of every
   * service's schema, listed once.
   */
  readonly customerJobMetadata: Readonly<Record<string, CustomerJobMetadataPolicy>>;
  readonly actions?: Array<TenantActionConfig>;
  readonly invoiceNumberMinimum?: number;
  readonly invoiceSuperscript?: boolean;
  readonly invoiceDiscounted?: boolean;
  readonly autorespondStart?: TTime24;
  readonly autorespondEnd?: TTime24;
  readonly autorespondMessage?: string;
  readonly autorespondWeekend?: boolean;
  readonly autorespondKeywords?: Record<string, string>;
  readonly autorespondHolidays?: Partial<Record<Holiday, string>>;
  readonly skipAddress?: (metadata: Record<string, unknown>) => boolean;
  readonly deliveryOrigin?: GeocodedAddress;
  readonly deliveryStart?: () => DateTime;
  readonly isAddressValid?: (address: DistanceAddress) => { valid: true } | { valid: false; message: string };
  readonly wizardDayFilter?: (date: DateTime, context: C) => boolean;
  readonly wizardRequestedFirst?: (context: C) => boolean;
  readonly showCalculator?: boolean;
  readonly brands?: Record<string, TenantBrand>;
  /**
   * "Ask Twist Support", the office's AI support panel (.server/support).
   * Absent = off: the panel is not rendered and /vendor/support answers 404.
   * On for twt only.
   */
  readonly supportAssistant?: {
    /** The panel's name, as the office sees it ("Ask Twist Support"). */
    title: string;
    /** Monthly model spend cap in USD (default 50). */
    monthlyCapUsd?: number;
    /**
     * The email channel (.server/support/email): the office forwards an
     * agent's email and gets the answer back by email. Absent = no email
     * channel. Addresses default to SUPPORT_EMAIL_DEFAULT_* in
     * .server/support/config.ts.
     */
    email?: {
      /** The address the Cloudflare Email Worker receives on (envelope recipient). */
      inbound?: string;
      /**
       * The address the office writes to and replies to (Twist:
       * ask@twisttours.com; Google reroutes it to `inbound`). Inbound mail
       * must carry it in a DKIM-signed To or Cc.
       */
      replyTo?: string;
      /**
       * 'observe' (the default): verify and log the header layout, never run
       * the model or reply. 'active': answer. Switch after one real test
       * forward confirms Cloudflare's results header is on top.
       */
      mode?: 'observe' | 'active';
    };
  };
}

const TENANTS = new Map<string, TenantConfig<any>>();

export function registerTenant<Id extends TenantId, C extends TenantOrderContext<any, any, any>>(
  id: Id,
  orderSchema: ZodVersionedSchema,
  other: Omit<TenantConfig<C>, 'id' | 'orderSchema'>,
): TenantConfig<C> {
  const config = {
    id,
    orderSchema,
    ...other,
  };

  TENANTS.set(id, config);

  return config;
}

export function getTenantByDomain(domain: string): TenantConfig | null {
  for (const tenant of TENANTS.values()) {
    if (tenant.domain === domain || Object.values(tenant.brands ?? {}).some((brand) => brand.domain === domain)) {
      return tenant;
    }
  }

  return null;
}

/** Every registered tenant — the Platform console's portfolio view. */
export function getTenants(): TenantConfig[] {
  return [...TENANTS.values()];
}

export function getTenant(id: TenantId): TenantConfig {
  const tenant = TENANTS.get(id);

  if (!tenant) {
    throw new Error(`Tenant with id "${id}" was not found.`);
  }

  return tenant;
}

export function createPerformableConfig<I extends string, S extends ZodVersionedSchema>(
  id: I,
  schema: S,
  config: Omit<TenantPerformableConfig<S, I>, 'id' | 'schema'>,
): TenantPerformableConfig<S, I> {
  return {
    id,
    schema,
    ...config,
  };
}

export function getPackage(tenant: TenantConfig, packageId: string) {
  const pkg = tenant.packages.find(({ config }) => config.id === packageId);

  if (!pkg) {
    throw new Error(`Package ${packageId} was not found.`);
  }

  return pkg;
}
